import { createContext, useCallback, useContext, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "meritz_subscriber_v1";

const OrderCaptureContext = createContext(null);

export function useOrderCapture() {
  const ctx = useContext(OrderCaptureContext);
  if (!ctx) throw new Error("useOrderCapture must be used inside OrderCaptureProvider");
  return ctx;
}

export function OrderCaptureProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const alreadyOptedIn = () => {
    try {
      return localStorage.getItem(LS_KEY) === "1";
    } catch {
      return false;
    }
  };

  const openWa = useCallback((url) => {
    if (!url) return;
    // Open in a new tab. window.open without the noopener feature returns a
    // window reference, which we then null its opener to avoid reverse-tabnabbing.
    // (Using 'noopener,noreferrer' as a feature string returns null per spec and
    // would force the location.href fallback to run — that navigates the host
    // page away, which we don't want.)
    try {
      const w = window.open(url, "_blank");
      if (w) {
        try {
          w.opener = null;
        } catch {}
        return;
      }
    } catch {}
    // Popup was blocked — last resort: same-tab navigation.
    window.location.href = url;
  }, []);

  const requestOrder = useCallback(
    (waUrl) => {
      if (alreadyOptedIn()) {
        openWa(waUrl);
        return;
      }
      setTarget(waUrl);
      setOpen(true);
    },
    [openWa],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    if (form.phone.trim().length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        consent: true,
        source: "order_gate",
      });
      try {
        localStorage.setItem(LS_KEY, "1");
      } catch {}
      toast.success("Thanks! Opening WhatsApp…");
      setOpen(false);
      const url = target;
      setTarget(null);
      setForm({ name: "", phone: "", email: "" });
      // Allow toast to render then redirect
      setTimeout(() => openWa(url), 250);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save — please try again";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({ requestOrder }), [requestOrder]);

  return (
    <OrderCaptureContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="order-gate-dialog"
          className="sm:max-w-md bg-[#F9F6F0] border-[#E8E2D6]"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <span className="w-11 h-11 rounded-full bg-[#C73E3A] text-white grid place-items-center">
                <MessageCircle size={20} />
              </span>
              <div>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                  One quick thing
                </DialogTitle>
                <DialogDescription className="text-[#6B635A]">
                  Drop your name & phone so we can send you our weekly specials.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3 mt-1">
            <Field label="Full name">
              <input
                data-testid="order-gate-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Chioma O."
                autoFocus
                className={inputClass}
                required
              />
            </Field>
            <Field label="Phone (WhatsApp)">
              <input
                data-testid="order-gate-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+234 ..."
                className={inputClass}
                required
              />
            </Field>
            <Field label="Email (optional)">
              <input
                data-testid="order-gate-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className={inputClass}
              />
            </Field>

            <p className="flex items-start gap-2 text-xs text-[#6B635A] pt-1">
              <Lock size={12} className="mt-0.5 shrink-0" />
              We only use this to send you specials & order updates. Unsubscribe anytime.
            </p>

            <button
              type="submit"
              disabled={loading}
              data-testid="order-gate-submit"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb854] disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-colors"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Saving…" : "Continue to WhatsApp"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </OrderCaptureContext.Provider>
  );
}

const inputClass =
  "w-full h-11 px-4 rounded-xl border border-[#E8E2D6] bg-white font-medium text-[#2C2825] placeholder:text-[#6B635A]/70 focus:outline-none focus:border-[#C73E3A] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-wider uppercase text-[#6B635A] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
